import { V1AreasOfImprovement } from "src/pages/reports/fsci/v1/types";

export const meta: V1AreasOfImprovement = {
    expertiseAndResources: {
        bestPracticeOrientation: {
            title: "Best Practice Orientation",
            prologue:
                "Keeping up with best practices in teaching, and identifying resources for doing so, can be better prioritized at the school.",
        },
        classroomManagement: {
            title: "Classroom Management",
            prologue:
                "Classroom management at the school can be improved by fostering more orderly classroom environments, better organization on the part of teachers, and/or consistency with discipline.",
        },
        engagedLearning: {
            title: "Engaged Learning",
            prologue:
                "Students do not engage in activities that nurture critical thinking, evaluating information, and problem solving as often as would be ideal.",
        },
        qualifiedStaff: {
            title: "Qualified Staff",
            prologue:
                "Hiring qualifications for staff (including education, licensure/certification, and prior classroom experience) can be improved.",
        },
        resourceConstraint: {
            title: "Resource Constraints",
            prologue:
                "A belief is held that the school could be more effective if not for fiscal constraints, and that the school lacks the resources needed to make changes in the school.",
        },
        resourcePlanning: {
            title: "Resource Planning",
            prologue:
                "Financial planning is an area of improvement for the school board, and/or strategic planning relative to finances or facilities is needed. ",
        },
        resources: {
            title: "Resources",
            prologue:
                "Educators lack some of the materials and resources needed for teaching, including technology, and the school building may require updates.",
        },
        responsivenessToSpecialNeeds: {
            title: "Responsiveness to Special Needs",
            prologue:
                "The way the school serves students with special needs can be improved, through efforts like greater collaboration among teachers and better processes for identifying and responding to student needs.",
        },
    },
    learningOrientation: {
        collaboration: {
            title: "Collaboration",
            prologue:
                "A more collaborative focus among teachers can be developed, which would help to inspire and drive better teaching.",
        },
        cultureOfImprovement: {
            title: "Culture of Improvement",
            prologue:
                "A culture of improvement can be better fostered at the school (including aspects like focusing on the future, continually working to improve, and making necessary changes to improve).",
        },
        dataDrivenImprovement: {
            title: "Data-Driven Improvement",
            prologue:
                "The school has room to better leverage data in gauging school results and effectiveness, determining goal attainment, and addressing problems faced by the school.",
        },
        feedback: {
            title: "Feedback",
            prologue:
                "Feedback on teaching practice and classroom management is needed on a more regular basis, to help facilitate adjustments in real time.",
        },
        individualizedInstruction: {
            title: "Individualized Instruction",
            prologue:
                "Instruction tends toward one-size-fits-all, and students are in need of additional support when it comes to figuring out how they learn best and identifying their natural strengths.",
        },
        outcomesFocus: {
            title: "Outcomes Focus",
            prologue:
                "Leadership may experience increased effectiveness by focusing more intentionally on outcomes (including student achievement) and results, rather than process and activity.",
        },
        professionalDevelopment: {
            title: "Professional Development",
            prologue:
                "There is room for improvement in PD efforts (e.g. providing PD on site and tailoring it to participants' specific subjects and roles).",
        },
        systemsThinking: {
            title: "Systems Thinking",
            prologue:
                "In planning for change, the school will benefit from increased consideration of potential impact on the classroom, students, and the overall school ecosystem.",
        },
    },
    purpose: {
        godsStory: {
            title: "God's Story",
            prologue:
                'Students need help to see the "bigger picture" of how they fit into God\'s plan, and how they can be used by him to "make a difference."',
        },
        holisticTeaching: {
            title: "Holistic Teaching",
            prologue:
                "Teachers are less inclined to view their work with students in a holistic way (e.g., helping students develop spiritually and emotionally, in addition to academic learning).",
        },
        integratedWorldView: {
            title: "Integrated Worldview",
            prologue:
                "Leadership could strengthen their belief in the educational centrality of Christian worldview across all spheres or areas of life.",
        },
        partnership: {
            title: "Partnership",
            prologue:
                "Many parents don't yet feel connected to the school’s mission, or that their child’s spiritual development requires their partnering with and being involved at the school.",
        },
        questioning: {
            title: "Questioning",
            prologue:
                "Many students view Christians negatively, and are more likely to be facing a time of spiritual disengagement or doubt.",
        },
        responsibility: {
            title: "Responsibility",
            prologue:
                "Teachers are less inclined to view their work with students in a holistic way (e.g., helping students develop spiritually and emotionally, in addition to academic learning).",
        },
        spiritualFormation: {
            title: "Spiritual Formation",
            prologue:
                "Alumni are less likely to view reliance on God as key to personal growth, and may not readily view their school as having strengthened their faith.",
        },
    },
    relationships: {
        caringEnvironment: {
            title: "Caring Environment",
            prologue:
                "Caring for and including students in class, as well as addressing bullying, can help improve students' perceptions of the school environment.",
        },
        christlikeTeachers: {
            title: "Christlike Teachers",
            prologue:
                "Students and their families perceive a need for greater individualized attention and loving care from teachers.",
        },
        communityEngagement: {
            title: "Community Engagement",
            prologue:
                "Better engagement with the surrounding community can be achieved through efforts like tapping into community resources, networking, and sharing resources with other schools.",
        },
        insularCulture: {
            title: "Insular Culture",
            prologue:
                "The school is perceived by constituents as isolated from the community in one or more ways (e.g., overprotecting students, not engaging with the larger community, and/or lacking diversity in the study body).",
        },
        leadershipInterdependence: {
            title: "Leadership Interdependence",
            prologue:
                "Leaders (including the board) can become more interdependent through transparency about weaknesses and relying on others to offset those weaknesses. Bringing individuals with diverse backgrounds to the table will also promote leadership interdependence.",
        },
        mentoringStudents: {
            title: "Mentoring Students",
            prologue:
                "Students would benefit from staff efforts to point out their individual talents and show how they fit into God's bigger plan, as well as greater staff awareness of students' struggles at school or home.",
        },
        parentRelationships: {
            title: "Parent Relationships",
            prologue:
                "Cultivating positive relationships with parents, through frequent and systemic communication, will help parents to feel more connected with the school and with teachers.",
        },
        prosocialOrientation: {
            title: "Prosocial Orientation",
            prologue:
                "Students are less-oriented toward helping others, and would benefit from learning how to show love and care toward peers in consistent ways.",
        },
        supportiveLeadership: {
            title: "Supportive Leadership",
            prologue:
                "Leadership can provide greater support to educators at the school by building trust and empowering teachers and staff to make decisions.",
        },
    },
    wellBeing: {
        healthyLiving: {
            title: "Healthy Living",
            prologue: "Healthy living (including diet and exercise) is a concern for students.",
        },
        resilience: {
            title: "Resilience",
            prologue:
                "Students feel unable to respond well to stress and ill-equipped to work through difficult situations.",
        },
        stress: {
            title: "Stress",
            prologue:
                "Ways to improve teachers' and leaders' well-being should be explored, as educators feel stressed or overwhelmed in their roles.",
        },
    },
};
